
import React from "react";
import PageHeader from "./PageHeader";
import PageContent from "./PageContent";
import { useProjects } from "@/components/Auth/Projects/useProjects";
import ProjectViewSwitcher from "@/components/Projects/ProjectViewSwitcher";

interface MainContentProps {
  projectId: string | null;
  projectName: string;
  selectedFolderId: string | null;
  activeTab: string;
  activeSubTab: string;
  isSharedFolder: boolean;
  folderTreeKey: number;
  isLoading: boolean;
  fetchError: string | null;
  user: any;
  setActiveTab: (tab: string) => void;
  setActiveSubTab: (tab: string) => void;
  handleCreateFolder: () => void;
  handleProjectChange: (projectId: string) => Promise<void>;
  handleCreateProject: (name: string) => Promise<void>;
  handleSelectProject: (projectId: string) => Promise<string>;
  handleSelectSharedFolder: (folderId: string) => Promise<string>;
  handleCreateRootFolder: () => boolean;
  handleStartAssessment: (projectId: string, folderId: string) => void;
  handleBackToFolders: () => void;
  handleSelectFolder: (folderId: string) => void;
  handleNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  handleCreateNewAssessment: (projectId: string, folderId: string) => void;
  fetchProjectIdForSharedFolder: (folderId: string) => Promise<string | null>;
  refreshFolderTree: () => void;
}

const MainContent: React.FC<MainContentProps> = ({
  projectId,
  projectName,
  selectedFolderId,
  activeTab,
  activeSubTab,
  isSharedFolder,
  folderTreeKey,
  isLoading,
  fetchError,
  user,
  setActiveTab,
  setActiveSubTab,
  handleCreateFolder,
  handleProjectChange,
  handleCreateProject,
  handleSelectProject,
  handleSelectSharedFolder,
  handleCreateRootFolder,
  handleStartAssessment,
  handleBackToFolders,
  handleSelectFolder,
  handleNavigateToAssessment,
  handleCreateNewAssessment,
  fetchProjectIdForSharedFolder,
  refreshFolderTree
}) => {
  const { projects, sharedProjects, handleDeleteProject } = useProjects();
  const currentProject = projects.find(p => p.id === projectId);

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="flex-1 max-w-7xl mx-auto w-full">
        <div className="px-6 pt-6">
          <ProjectViewSwitcher />
        </div>
        <PageHeader
          projectId={projectId}
          projectName={projectName}
          selectedFolderId={selectedFolderId}
          selectedFolderName={currentProject?.name}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          folderTreeKey={folderTreeKey}
          onCreateFolder={handleCreateFolder}
          onProjectChange={handleProjectChange}
          onCreateProject={handleCreateProject}
          onNavigateToProject={() => { if (projectId) window.location.href = `/uppdrag/${projectId}`; }}
          onNavigateToFolder={() => { if (selectedFolderId && projectId) window.location.href = `/uppdrag/${projectId}`; }}
        />
        
        {/* Main Content Area - Full width */}
        <div className="p-6 space-y-6">
          <PageContent
            isLoading={isLoading}
            fetchError={fetchError}
            projectId={projectId}
            selectedFolderId={selectedFolderId}
            activeTab={activeTab}
            activeSubTab={activeSubTab}
            user={user}
            isSharedFolder={isSharedFolder}
            setActiveSubTab={setActiveSubTab}
            handleSelectProject={handleSelectProject}
            handleSelectSharedFolder={handleSelectSharedFolder}
            handleCreateRootFolder={handleCreateRootFolder}
            handleStartAssessment={handleStartAssessment}
            handleBackToFolders={handleBackToFolders}
            fetchProjectIdForSharedFolder={fetchProjectIdForSharedFolder}
            handleNavigateToAssessment={handleNavigateToAssessment}
            handleCreateNewAssessment={handleCreateNewAssessment}
            refreshFolderTree={refreshFolderTree}
            onSelectFolder={handleSelectFolder}
            projects={projects}
            sharedProjects={sharedProjects}
            onCreateProject={async (name: string) => {
              await handleCreateProject(name);
            }}
            onDeleteProject={(id: string) => handleDeleteProject(id, true)}
          />
        </div>
      </main>
    </div>
  );
};

export default MainContent;
